import React, { useState } from "react";
import {
  IndianRupee, ChevronRight, CreditCard, Download, RotateCcw,
} from "lucide-react";
import { InfoCard, EmptyState } from "../ui";
import RefundModal from "../ui/RefundModal";
import { convertToIndianCurrency } from "../../utils/common";
import apiClient from "../../utils/apiClient";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

/** Download Razorpay invoice PDF for a given fee transaction. */
async function downloadInvoice(feeId) {
  const res = await apiClient.get(`/payments/${feeId}/invoice`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-${feeId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Gateway / status badge helper
 */
function GatewayBadge({ gateway, status }) {
  if (status === "refunded") return <span className="sp-fee-badge sp-fee-badge--refunded">Refunded</span>;
  if (status === "partially_refunded") return <span className="sp-fee-badge sp-fee-badge--partial-refund">Partial Refund</span>;
  const isOnline = gateway === "razorpay";
  const cls = isOnline
    ? status === "success" ? "sp-fee-badge sp-fee-badge--success" : "sp-fee-badge sp-fee-badge--pending"
    : "sp-fee-badge sp-fee-badge--manual";
  return (
    <span className={cls}>
      {isOnline ? (status === "success" ? "Online ✓" : "Online") : "Manual"}
    </span>
  );
}

/**
 * StudentPayments — Fee history card for student detail page.
 *
 * Props:
 *   payments   — Array of StudentFee transaction objects
 *   onViewAll  — Handler for "View All" button
 */
const StudentPayments = ({ payments = [], onViewAll, onRefundSuccess }) => {
  const [downloading, setDownloading] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState("");

  const handleDownload = async (e, feeId) => {
    e.stopPropagation();
    setDownloading(feeId);
    try {
      await downloadInvoice(feeId);
    } catch {
      // silent
    }
    setDownloading(null);
  };

  const isRazorpayPaid = (p) =>
    p.paymentGateway === "razorpay" && p.paymentStatus === "success";

  const canRefund = (p) =>
    p.paymentStatus === "success" || p.paymentStatus === "partially_refunded";

  const handleRefund = async (reason, amount) => {
    if (!refundTarget) return;
    setRefundLoading(true);
    setRefundError("");
    try {
      const isOnline = refundTarget.paymentGateway === "razorpay";
      const endpoint = isOnline
        ? `/payments/${refundTarget._id}/refund`
        : `/fees/${refundTarget._id}/refund`;
      await apiClient.post(endpoint, { reason, amount });
      setRefundTarget(null);
      if (onRefundSuccess) onRefundSuccess();
    } catch (err) {
      setRefundError(err?.response?.data?.message || "Failed to refund fee transaction.");
    }
    setRefundLoading(false);
  };

  const toggleExpand = (id) =>
    setExpanded((prev) => (prev === id ? null : id));

  return (
    <InfoCard
      icon={<CreditCard size={16} />}
      iconClass="emerald"
      title="Fee History"
      action={
        onViewAll && (
          <button className="ui-card__action" onClick={onViewAll}>
            View All <ChevronRight size={10} />
          </button>
        )
      }
    >
      {payments.length > 0 ? (
        payments.slice(0, 5).map((p) => (
          <div key={p._id} className="sp-fee-item-wrap">
            {/* Summary row — clickable to expand */}
            <div
              className={`ui-list-item sp-fee-item${expanded === p._id ? " sp-fee-item--active" : ""}`}
              onClick={() => toggleExpand(p._id)}
              role="button"
              tabIndex={0}
            >
              <div className="sp-pay-icon">
                <IndianRupee size={16} />
              </div>
              <div className="ui-list-item__info">
                <div className="sp-pay-amount">{convertToIndianCurrency(p.currentPayment)}</div>
                <div className="ui-list-item__sub">
                  {p.paymentMode || "—"}
                  {" · "}
                  {formatDate(p.date || p.createdAt)}
                </div>
              </div>
              <div className="ui-list-item__right">
                <GatewayBadge gateway={p.paymentGateway} status={p.paymentStatus} />
                {isRazorpayPaid(p) && (
                  <button
                    className="sp-fee-download-btn"
                    title="Download Receipt"
                    disabled={downloading === p._id}
                    onClick={(e) => handleDownload(e, p._id)}
                  >
                    <Download size={14} />
                  </button>
                )}
                {canRefund(p) && (
                  <button
                    className="sp-fee-download-btn sp-fee-refund-btn"
                    title="Refund"
                    onClick={(e) => { e.stopPropagation(); setRefundTarget(p); }}
                  >
                    <RotateCcw size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Expanded detail panel */}
            {expanded === p._id && (
              <div className="sp-fee-detail">
                <div className="sp-fee-detail__grid">
                  {p.paymentMode && (
                    <div className="sp-fee-detail__item">
                      <span className="sp-fee-detail__label">Mode</span>
                      <span className="sp-fee-detail__value">{p.paymentMode}</span>
                    </div>
                  )}
                  {p.paymentGateway && (
                    <div className="sp-fee-detail__item">
                      <span className="sp-fee-detail__label">Gateway</span>
                      <span className="sp-fee-detail__value">
                        {p.paymentGateway === "razorpay" ? "Razorpay" : "Manual"}
                      </span>
                    </div>
                  )}
                  <div className="sp-fee-detail__item">
                    <span className="sp-fee-detail__label">Date</span>
                    <span className="sp-fee-detail__value">{formatDate(p.date || p.createdAt)}</span>
                  </div>
                  {p.paidTo && (
                    <div className="sp-fee-detail__item">
                      <span className="sp-fee-detail__label">Paid To</span>
                      <span className="sp-fee-detail__value">{p.paidTo}</span>
                    </div>
                  )}
                  {p.referenceNumber && (
                    <div className="sp-fee-detail__item sp-fee-detail__item--full">
                      <span className="sp-fee-detail__label">Transaction ID</span>
                      <span className="sp-fee-detail__value sp-fee-detail__value--mono">
                        {p.referenceNumber}
                      </span>
                    </div>
                  )}
                  {p.razorpayPaymentId && (
                    <div className="sp-fee-detail__item sp-fee-detail__item--full">
                      <span className="sp-fee-detail__label">Razorpay Payment ID</span>
                      <span className="sp-fee-detail__value sp-fee-detail__value--mono">
                        {p.razorpayPaymentId}
                      </span>
                    </div>
                  )}
                  {p.comments && (
                    <div className="sp-fee-detail__item sp-fee-detail__item--full">
                      <span className="sp-fee-detail__label">Comments</span>
                      <span className="sp-fee-detail__value">{p.comments}</span>
                    </div>
                  )}
                  {(p.paymentStatus === "refunded" || p.paymentStatus === "partially_refunded") && (
                    <div className="sp-fee-detail__item sp-fee-detail__item--full">
                      <span className="sp-fee-detail__label">Refund</span>
                      <span className="sp-fee-detail__value sp-fee-detail__value--refund">
                        {convertToIndianCurrency(p.refundAmount || p.currentPayment)}
                        {p.refundReason ? ` — ${p.refundReason}` : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))
      ) : (
        <EmptyState
          icon={<CreditCard size={20} />}
          title="No payments yet"
        />
      )}

      <RefundModal
        show={!!refundTarget}
        onHide={() => { if (!refundLoading) { setRefundTarget(null); setRefundError(""); } }}
        onConfirm={handleRefund}
        error={refundError}
        title="Refund Payment"
        studentName=""
        originalAmount={refundTarget?.currentPayment || 0}
        maxRefundable={(refundTarget?.currentPayment || 0) - (refundTarget?.refundAmount || 0)}
        isOnline={refundTarget?.paymentGateway === "razorpay"}
        loading={refundLoading}
      />
    </InfoCard>
  );
};

export default StudentPayments;
